import { Component } from "@angular/core";
import { BsModalRef } from "ngx-bootstrap/modal";
import { AutomationProgressUpdate, AutomationRunError, AutomationRunHandle } from "@services/rest/automation-api.service";

interface StepLogEntry {
    stepId: string;
    stepLabel: string;
    // True for the synthetic "login already completed" entry seeded in bindHandle() - login is a
    // separate, earlier action (its own button/session, reused across many create-quotation runs -
    // see try_reuse_etiqa_session()) whose own steps (1-11b) aren't tracked by this run at all, so
    // this is a cosmetic placeholder only, not a real polled step.
    isSummary?: boolean;
}

interface DetailField {
    label: string;
    value: string;
}

// ETIQA step id -> the request-body field(s) that step fills/selects on the portal, purely for
// the operator-facing "what value did Angular send for this step" expand panel - there is no
// backend change involved, this just replays a subset of the same request body
// runEtiqaCreateQuotationFor() already sent (see etiqa2.py's own run_step() call sites for the
// step id <-> field mapping this mirrors). Steps with no entry here (pure clicks/checkbox ticks
// with no Angular-supplied value) simply get no expand affordance. Not exhaustive - covers the
// fields that matter for spot-checking what was sent.
export const ETIQA_STEP_FIELDS: Record<string, string[]> = {
    "12": ["vehicleRegNo"],
    "12a": ["ownerNameType"],
    "12b": ["usageButtonText"],
    "13": ["postcode"],
    "11c-2": ["ownerNameType"],
    "11c-6": ["idNumber"],
    "11c-7": ["companyName"],
    "14": ["idTypeKeywords"],
    "15": ["idNumber"],
    "15b": ["nationality", "dob"],
    "17-mc": ["useZeroNcd"],
    "18-mc": ["coverType"],
    "19-mc": ["sumInsuredType"],
    "19-car": ["customAgreedValue"],
    "20-car": ["extraCoverages", "windscreenSumInsured", "personalAccidentDesc"],
    "11c-17": ["coverType"],
    "11c-18": ["sumInsuredType"],
    "11c-19": ["customAgreedValue"],
    "11c-21": ["extraCoverages", "windscreenSumInsured"],
    "22": ["customerName"],
    "23": ["ethnicityKeywords"],
    "24": ["maritalStatusKeywords"],
    "25": ["contactNo"],
    "26": ["email"],
    "27": ["address1"],
    "28": ["address2"],
};

const FIELD_LABELS: Record<string, string> = {
    vehicleRegNo: "Vehicle Reg No.",
    ownerNameType: "Owner Name Type",
    usageButtonText: "Vehicle Usage",
    postcode: "Postcode",
    idNumber: "ID / Business Reg No.",
    companyName: "Company Name",
    idTypeKeywords: "ID Type",
    nationality: "Nationality",
    dob: "Date of Birth",
    useZeroNcd: "Use 0% NCD",
    coverType: "Cover Type",
    sumInsuredType: "Sum Insured Type",
    customAgreedValue: "Custom Agreed Value",
    extraCoverages: "Extra Coverages",
    windscreenSumInsured: "Windscreen Sum Insured",
    personalAccidentDesc: "Personal Accident Plan",
    customerName: "Customer Name",
    ethnicityKeywords: "Ethnicity",
    maritalStatusKeywords: "Marital Status",
    contactNo: "Contact No.",
    email: "Email",
    address1: "Address Line 1",
    address2: "Address Line 2",
    // Tokio Marine (tokiomarine.py) - Create + Update Quotation, see TOKIO_MARINE_CREATE_STEP_FIELDS
    // / TOKIO_MARINE_UPDATE_STEP_FIELDS below.
    identityType: "ID Type",
    identity: "ID / Business Reg No.",
    registrationNo: "Vehicle Reg No.",
    gender: "Gender",
    birthYear: "Year of Birth",
    vehicleType: "Vehicle Type",
    selectPlanSelector: "Plan Selector",
    coverageType: "Coverage Type",
    nvic: "NVIC / Variant",
    seat: "Seating Capacity",
    searchName: "Customer Name",
    quoteId: "Quotation No.",
    sumInsured: "Sum Insured",
    // Zurich (zurich.py) - Create Quotation only, see ZURICH_CREATE_STEP_FIELDS below. vehicleRegNo
    // already has a label above (ETIQA_STEP_FIELDS' own field of the same name).
    ic1: "NRIC (part 1)",
    ic2: "NRIC (part 2)",
    ic3: "NRIC (part 3)",
    passportNo: "Passport / Business Reg No.",
    // Takaful Ikhlas (takafulikhlas.py) - see TAKAFUL_CREATE_STEP_FIELDS/TAKAFUL_EDIT_STEP_FIELDS
    // below. ic1/ic2/ic3/passportNo/nationality/dob/vehicleType/vehicleRegNo/coverType/contactNo/
    // extraCoverages/windscreenSumInsured/postcode/address1/address2/sumInsured already have
    // labels above (Zurich's/Tokio Marine's/ETIQA's own fields of the same name).
    nameTitle: "Salutation",
    name: "Name",
};

// Tokio Marine (tokiomarine.py) create-quotation step id -> request-body field(s), same purpose/
// convention as ETIQA_STEP_FIELDS above - not exhaustive, covers the fields worth spot-checking.
export const TOKIO_MARINE_CREATE_STEP_FIELDS: Record<string, string[]> = {
    "8": ["identity"],
    "10": ["identityType", "searchName"],
    "10b": ["nationality", "gender"],
    "11": ["identity"],
    "11b": ["birthYear"],
    "12": ["postcode"],
    "13b": ["vehicleType"],
    "14": ["registrationNo"],
    "16a": ["coverageType", "selectPlanSelector", "nvic"],
    "17": ["extraCoverages", "windscreenSumInsured"],
    "17b": ["seat"],
};

// Tokio Marine (tokiomarine.py) update-quotation step id -> request-body field(s).
export const TOKIO_MARINE_UPDATE_STEP_FIELDS: Record<string, string[]> = {
    "1": ["quoteId"],
    "4a": ["coverageType", "selectPlanSelector", "nvic"],
    "4d": ["sumInsured"],
    "5": ["extraCoverages", "windscreenSumInsured"],
};

// Kurnia (kurnia.py) create-quotation step id -> request-body field(s), same purpose/convention
// as ETIQA_STEP_FIELDS/TOKIO_MARINE_CREATE_STEP_FIELDS above - not exhaustive.
export const KURNIA_CREATE_STEP_FIELDS: Record<string, string[]> = {
    "6": ["identityType"],
    "7": ["identity"],
    "10a": ["identityType"],
    "10b": ["identity"],
    "10c": ["identity"],
    "10e": ["clientName"],
    "10f": ["nationality"],
    "10i": ["address1"],
    "10j": ["address2"],
    "10k": ["postcode"],
    "12b": ["vehicleType"],
    "13": ["coverType"],
    "15": ["registrationNo"],
    "21b": ["sumInsured"],
    "22b": ["extraCoverages", "windscreenSumInsured"],
    "22c": ["extraCoverages"],
};

// Kurnia (kurnia.py) update-quotation step id -> request-body field(s).
export const KURNIA_UPDATE_STEP_FIELDS: Record<string, string[]> = {
    "9": ["coverType"],
    "14": ["sumInsured"],
    "16": ["extraCoverages", "windscreenSumInsured"],
};

// Allianz (allianz.py) create-quotation step id -> request-body field(s), same purpose/convention
// as ETIQA_STEP_FIELDS/TOKIO_MARINE_CREATE_STEP_FIELDS/KURNIA_CREATE_STEP_FIELDS above - not
// exhaustive. allianz.py has no separate Update Quotation automation yet - Create Quotation is the
// only Allianz flow that shows this modal (onAllianzUpdateQuotation() just shows a "not automated
// yet" notification).
export const ALLIANZ_CREATE_STEP_FIELDS: Record<string, string[]> = {
    "14": ["registrationNo"],
    "15": ["identityType"],
    "16": ["identity"],
    "21": ["contactNo"],
    "22": ["email"],
    "23": ["postcode"],
    "27": ["coverType"],
    "29": ["sumInsured"],
    "31": ["extraCoverages", "windscreenSumInsured"],
};

// Zurich (zurich.py) create-quotation step id -> request-body field(s), same purpose/convention as
// ALLIANZ_CREATE_STEP_FIELDS above - not exhaustive. zurich.py only covers steps 1-12 so far (open
// the zMotor landing page through click Get Quote - see zurich.py's own module docstring), and has
// no separate Update Quotation automation.
export const ZURICH_CREATE_STEP_FIELDS: Record<string, string[]> = {
    "3": ["vehicleRegNo"],
    "4": ["identityType"],
    "5": ["identityType"],
    "6": ["ic1", "ic2", "ic3", "passportNo"],
    "7": ["contactNo"],
    "10": ["customerName"],
    "11": ["postcode"],
    "16": ["extraCoverages", "windscreenSumInsured"],
};

// Takaful Ikhlas (takafulikhlas.py) create-quotation step id -> request-body field(s), same
// purpose/convention as ALLIANZ_CREATE_STEP_FIELDS above - not exhaustive.
export const TAKAFUL_CREATE_STEP_FIELDS: Record<string, string[]> = {
    "3": ["ic1", "ic2", "ic3", "passportNo", "nationality", "dob"],
    "4": ["nameTitle", "name"],
    "11": ["address1"],
    "12": ["address2"],
    "13": ["postcode"],
    "18": ["vehicleType"],
    "21": ["vehicleRegNo"],
    "24": ["coverType"],
    "34": ["contactNo"],
    "38": ["extraCoverages", "windscreenSumInsured"],
};

// Takaful Ikhlas (takafulikhlas.py) edit-quotation step id -> request-body field(s) - separate
// numbering from create-quotation above (edit's own steps 1-13, see takafulikhlas.py's
// _takaful_edit_quotation_impl()).
export const TAKAFUL_EDIT_STEP_FIELDS: Record<string, string[]> = {
    "1": ["vehicleRegNo"],
    "4c": ["vehicleType"],
    "5": ["coverType"],
    "8": ["sumInsured"],
    "10": ["extraCoverages", "windscreenSumInsured"],
};

// Best-effort "how many steps will THIS run actually take" estimates, used purely for the
// "Step N of M" header/hint text - not a real backend contract, just this request body's branch
// flags (vehicleType/identityType/...) replayed against the same conditions etiqa2.py's own
// _etiqa2_create_quotation_impl() branches on (is_motorcycle / is_car_company / is_passport /
// useZeroNcd), counted from its run_step() call sites. A run can still finish short of the
// estimate (e.g. the ID-search finds an existing customer and skips steps that assumed it
// wouldn't - see tokiomarine.py's own equivalent below) - that's fine, it just means the bar
// doesn't reach 100% right before jumping to Completed, which is far less confusing than no
// denominator at all.
export function computeEtiqaTotalSteps(body: Record<string, unknown>): number {
    const vehicleType = String(body.vehicleType ?? "").toLowerCase();
    const isMotorcycle = vehicleType.includes("motor");
    const identityType = String(body.identityType ?? "").toLowerCase();
    const isCompany = identityType === "company";
    const isCarCompany = !isMotorcycle && isCompany;
    const isMotorcycleCompany = isMotorcycle && isCompany;
    const idTypeKeywords = Array.isArray(body.idTypeKeywords) ? (body.idTypeKeywords as unknown[]) : [];
    const isPassport = !isCarCompany && idTypeKeywords.some((k) => String(k).toLowerCase().includes("passport"));

    if (isCarCompany) {
        // 11c-1, 11c-2, 12, 12b, 13, 11c-6..11c-16 (11), 14, 15, 16, 11c-17..11c-23 (7)
        return 26;
    }
    if (isMotorcycle) {
        // 12, [12a,] 12b, 13, 14, 15, 15a, [15a2,] 16, 16b/16c-16d-mc(-company), 18-mc, 19-mc, 20-mc, 21-mc
        let total = isMotorcycleCompany ? 14 : 13;
        if (!isMotorcycleCompany && isPassport) total += 1; // 15b
        if (body.useZeroNcd) total += 1; // 17-mc
        return total;
    }
    // car, individual: 12, 12b, 13, 14, 15, 16, 16b-car, 17-car..21-car (5), 22..31 (10)
    let total = 22;
    if (isPassport) total += 1; // 15b
    return total;
}

// Same purpose as computeEtiqaTotalSteps() above, mirroring tokiomarine.py's own
// _tokiomarine_create_quotation_impl() branches (identityType passport/company) - counted from
// its run_step() call sites: 8, 10, [10b,] 11, [11b,] 12, 13b, 14, 15, 15b, 16a, 16, 17, 17b, 18,
// 19, 19b, 23. Assumes the ID-number search does NOT find an existing customer (the longer path,
// see step "8"/"10"/"11" in tokiomarine.py) - if it does, the run finishes a few steps short of
// this estimate, same caveat as above.
export function computeTokioMarineCreateTotalSteps(body: Record<string, unknown>): number {
    const identityType = String(body.identityType ?? "").toLowerCase();
    let total = 16;
    if (identityType === "passport") {
        total += 1; // 10b
        if (body.birthYear) total += 1; // 11b
    } else if (identityType === "company") {
        total += 1; // 10b
    }
    return total;
}

// Mirrors tokiomarine.py's own _tokiomarine_update_quotation_impl() branches (coverageType
// CO/TP/TF) - counted from its run_step() call sites: 1, 4, 4a, 4b, [4b2,] [4c, 4d,] 5, 6, 19, 19+.
export function computeTokioMarineUpdateTotalSteps(body: Record<string, unknown>): number {
    const coverageCode = String(body.coverageType ?? "").trim().toUpperCase();
    let total = 8;
    if (coverageCode !== "TP") total += 2; // 4c, 4d
    if (coverageCode === "" || coverageCode === "CO") total += 1; // 4b2
    return total;
}

// Same purpose as computeEtiqaTotalSteps() above, counted from kurnia.py's own
// _kurnia_create_quotation_impl() run_step() call sites: 1, 2, 3, 4, 5, [6, 7, 8, 9,] 10,
// [10a..10k (11),] 11, 12, 12b, 13, 13b, 14, 15, 16, 17, 18, 18b, 19, 19b, 19c, 20, 21, 21b, 21c,
// 22, 22b, 22c, 23, 23b, 23c, 24, 25, 26. Assumes the client-search finds no existing record (the
// longer "scenario 2" path that fills in 10a-10k) - if a record IS found instead, the run finishes
// ~11 steps short of this estimate, same "can finish short" caveat as every other insurer's own
// estimate here. The optional inception-date-correction sub-steps (19b's NCD-% variant, 19d-19g)
// aren't counted at all, same reasoning as etiqa's own optional sub-steps.
export function computeKurniaCreateTotalSteps(body: Record<string, unknown>): number {
    const hasIdentitySearch = !!(body.identity && body.identityType);
    let total = 5; // 1-5
    if (hasIdentitySearch) total += 4; // 6, 7, 8, 9
    total += 1; // 10 (branch)
    total += 11; // 10a-10k (scenario 2 assumed)
    total += 3; // 11, 12, 12b
    total += 2; // 13, 13b
    total += 9; // 14, 15, 16, 17, 18, 18b, 19, 19b, 19c
    total += 4; // 20, 21, 21b, 21c
    total += 3; // 22, 22b, 22c
    total += 3; // 23, 23b, 23c
    total += 3; // 24, 25, 26
    return total;
}

// Kurnia's own update-quotation flow (kurnia.py's _kurnia_update_quotation_impl()) is comparatively
// linear - steps 1-9, 9b, 10-21 all run unconditionally (even the ones that just log "skipping" on
// a Refer Risk quote or when there's nothing new to add), so a fixed count is accurate here rather
// than a best-effort branch-dependent estimate.
export function computeKurniaUpdateTotalSteps(): number {
    return 23;
}

// allianz.py's own _allianz_create_quotation_impl() always runs steps 8-19 (declaration through
// customer search - tickDeclarationCheckbox/answerDeclarationQuestionsNo default true and aren't
// overridden by onAllianzCreateQuotation()'s own request body, so steps 9-12 always run too), then
// branches on the customer-search outcome. This assumes the longer "Customer Information is not
// complete" branch (steps 20-25 fill in the code/mobile/email/postcode gap and extract vehicle
// details/policy dates) rather than the shorter "Customer Found" branch (which skips straight from
// step 20's Use of Vehicle into the shared tail) - if Customer Found is what the portal actually
// returns instead, the run finishes ~5 steps short of this estimate, same "can finish short"
// caveat as every other insurer's own estimate here. Steps 26-34 (shared tail: coverage-type
// selection through the final Premium Summary table) always run once either branch completes.
export function computeAllianzCreateTotalSteps(): number {
    return 27;
}

// zurich.py's own _zurich_create_quotation_impl() always runs steps 1-12 unconditionally (the
// Company/Passport radio picks and the NRIC/passport fill are all no-op "leave the page default"
// branches rather than skipped steps - see zurich.py's own step 4/5/6 comments), so a fixed count
// is accurate here rather than a best-effort branch-dependent estimate, same reasoning as
// computeKurniaUpdateTotalSteps() above.
export function computeZurichCreateTotalSteps(): number {
    return 16;
}

// takafulikhlas.py's own _takaful_create_quotation_impl() run_step() call sites, assuming the
// full path completes (chassis-no wait step 22, the NVIC popup auto-matches at step 30, and Add
// Extra Benefit is reached at step 38): 1-21 (21), 22, 23-41 (19) = 41. A run that stops early -
// a TakafulBusinessError at steps 28/30/37/38, or the NVIC popup left open for manual selection -
// finishes short of this estimate, same "can finish short" caveat as every other insurer's own
// estimate here.
export function computeTakafulCreateTotalSteps(): number {
    return 41;
}

// takafulikhlas.py's own _takaful_edit_quotation_impl() run_step() call sites, assuming the full
// path completes (Modify found at step 3, NVIC popup auto-matches at step 6, Add Extra Benefit
// reached at step 10): 1, 2, 3, 4, 4c, 4d, 5, 6, 6b, 7, 8, 9, 10, 11, 12, 13 = 16. A run that
// stops early (no Modify button, a TakafulBusinessError, or the NVIC popup left open) finishes
// short of this estimate, same "can finish short" caveat as every other insurer's own estimate.
export function computeTakafulEditTotalSteps(): number {
    return 16;
}

// camelCase -> "Camel Case", used as a fallback label for result/detail keys not in FIELD_LABELS
// above (e.g. the raw keys inside result.pricingBreakdown / result.vehicleInfo) so this doesn't
// need to be kept in lockstep with every field etiqa2.py's own result dict happens to return.
function humanizeKey(key: string): string {
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/^./, (c) => c.toUpperCase());
}

function formatValue(value: unknown): string {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.length ? value.map((v) => formatValue(v)).join(", ") : "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

// Shows live step progress for a pausable/resumable automation run (see
// AutomationApiService.runCreateQuotation()) and exposes Pause/Continue/Abort controls wired to
// the run's control endpoints. Opened via bsModalService.show(...) the same way every other
// modal in this codebase is (ConfirmationModalComponent, InvoiceRefundModalComponent, ...) -
// the caller binds it to a live run right after opening it via bindHandle(), then awaits
// runCreateQuotation()'s own promise and hides this modal once that resolves.
@Component({
    templateUrl: "step-progress-modal.component.html",
    styleUrls: ["step-progress-modal.component.css"],
    standalone: false
})
export class StepProgressModalComponent {
    insurerDisplayName: string = "";
    // Distinguishes multiple actions on the same insurer (e.g. Tokio Marine's Create Quotation vs
    // Update Quotation, each its own modal instance) - shown in the header as
    // "{{insurerDisplayName}} - {{actionLabel}}". Defaults to "Create Quotation" so the existing
    // ETIQA call site (which only sets insurerDisplayName) needs no change.
    actionLabel: string = "Create Quotation";

    status: AutomationProgressUpdate["status"] = "running";
    currentStep: string | null = null;
    currentStepLabel: string = "";
    error: AutomationRunError | null = null;
    // Client-side append-only log of steps seen so far, built purely from polled updates - there
    // is no server-side fixed step count/sequence, since the flow branches on vehicle type /
    // company ownership / ID type. totalSteps (see computeEtiqaTotalSteps() etc. above) is a
    // best-effort estimate of this specific run's branch, bound alongside requestBody.
    stepLog: StepLogEntry[] = [];
    totalSteps: number | null = null;
    // The flow's own extracted-from-portal payload, only populated once status is 'done' - see
    // AutomationProgressUpdate.result's own docstring.
    result: any = null;

    busy = false;

    modalRef: BsModalRef;
    private handle: AutomationRunHandle | null = null;
    // The request body runCreateQuotation() was called with - kept purely so the steps table can
    // show, per row, what value Angular actually sent for that step (see ETIQA_STEP_FIELDS
    // above). Not sent anywhere; just replayed back into the UI.
    private requestBody: Record<string, unknown> | null = null;
    // Which step -> field map stepDetailFields() reads from - defaults to ETIQA_STEP_FIELDS so
    // the existing ETIQA call site (bindHandle(handle, requestBody), no third arg) is unaffected.
    private stepFieldsMap: Record<string, string[]> = ETIQA_STEP_FIELDS;
    private expandedSteps = new Set<string>();

    constructor(private bsModalRef: BsModalRef) {
        this.modalRef = bsModalRef;
    }

    // Called by the caller immediately after this modal is opened - see
    // AutomationApiService.runCreateQuotation()'s onHandle callback, which is invoked
    // synchronously before the first status poll so no progress update is ever missed.
    // `requestBody` is the same object literal passed as runCreateQuotation()'s own `body` arg.
    // `stepFieldsMap` overrides which step -> field map stepDetailFields() reads from (see
    // TOKIO_MARINE_CREATE_STEP_FIELDS / TOKIO_MARINE_UPDATE_STEP_FIELDS above); omitted, it stays
    // on the default ETIQA_STEP_FIELDS. `totalSteps` is the "of M" denominator (see
    // computeEtiqaTotalSteps() etc. above); omitted, the header falls back to a plain running count.
    bindHandle(
        handle: AutomationRunHandle,
        requestBody?: Record<string, unknown>,
        stepFieldsMap?: Record<string, string[]>,
        totalSteps?: number,
    ) {
        this.stepLog.push({
            stepId: "login",
            stepLabel: `${this.insurerDisplayName} login completed`,
            isSummary: true,
        });
        this.handle = handle;
        this.requestBody = requestBody || null;
        if (stepFieldsMap) {
            this.stepFieldsMap = stepFieldsMap;
        }
        this.totalSteps = totalSteps ?? null;
        handle.onProgress((update) => this.applyProgress(update));
    }

    // Real automation steps completed/in-progress so far, excluding the cosmetic "login
    // completed" summary row bindHandle() always seeds stepLog with - stepLog.length on its own
    // over-counts by that one row (e.g. after 5 real steps, stepLog.length is 6).
    get completedStepCount(): number {
        return Math.max(0, this.stepLog.length - (this.stepLog.some((e) => e.isSummary) ? 1 : 0));
    }

    private applyProgress(update: AutomationProgressUpdate) {
        this.status = update.status;
        this.error = update.error;
        if (update.stepHistory) {
            // Rebuilt from the backend's own authoritative step_history every poll, rather than
            // diffing against currentStep - a 1s poll can land after 2+ steps have already
            // completed, and diffing only currentStep would silently drop every step in between
            // (see run_step()'s own step_history docstring). The synthetic "login completed" row
            // bindHandle() seeds stepLog with isn't part of step_history, so it's kept in place.
            const summaryRows = this.stepLog.filter((e) => e.isSummary);
            this.stepLog = [
                ...summaryRows,
                ...update.stepHistory.map((h) => ({ stepId: h.stepId, stepLabel: h.stepLabel })),
            ];
        } else if (update.currentStep && update.currentStep !== this.currentStep) {
            this.stepLog.push({ stepId: update.currentStep, stepLabel: update.currentStepLabel });
        }
        this.currentStep = update.currentStep;
        this.currentStepLabel = update.currentStepLabel;
        if (update.result) {
            this.result = update.result;
        }
    }

    // Step rows whose Angular-supplied value(s) can be shown - see stepFieldsMap. A step id with
    // no entry in the bound map simply shows no expand affordance.
    hasStepDetail(stepId: string): boolean {
        return this.stepDetailFields(stepId).length > 0;
    }

    isExpanded(stepId: string): boolean {
        return this.expandedSteps.has(stepId);
    }

    toggleExpand(stepId: string): void {
        if (this.expandedSteps.has(stepId)) {
            this.expandedSteps.delete(stepId);
        } else {
            this.expandedSteps.add(stepId);
        }
    }

    stepDetailFields(stepId: string): DetailField[] {
        const fieldNames = this.stepFieldsMap[stepId];
        if (!fieldNames || !this.requestBody) return [];
        return fieldNames
            .filter((name) => this.requestBody![name] !== null && this.requestBody![name] !== undefined && this.requestBody![name] !== "")
            .map((name) => ({ label: FIELD_LABELS[name] || humanizeKey(name), value: formatValue(this.requestBody![name]) }));
    }

    // Tokio Marine/ETIQA can still return status 'done' (a real quotation was created) while the
    // portal itself flagged something needing manual review - e.g. an NCD confirmation conflict
    // or a referral case (see tokiomarine.py's own failedReason comment: "success stays True
    // because Tokio Marine still prices it"). Surfaced above the extracted-value panel as its own
    // warning callout rather than buried as just another row - see resultEntries()'s own skip set.
    get resultWarning(): string | null {
        return this.result?.failedReason || null;
    }

    // Flattens result (the extracted-from-portal payload) into label/value rows for display once
    // status is 'done' - generic rather than hand-listing every field, so it doesn't need updating
    // whenever etiqa2.py's own result dict gains/renames a field. Nested objects (pricingBreakdown,
    // vehicleInfo) are shown as their own indented sub-rows; `success`/`planOptions` are left out
    // (success is implied by reaching this screen, planOptions is an internal list already
    // reflected by `planName`); `failedReason` is left out too - shown separately via
    // resultWarning above, not as just another row.
    resultEntries(): { label: string; value: string; nested?: DetailField[] }[] {
        if (!this.result) return [];
        const skip = new Set(["success", "planOptions", "failedReason"]);
        return Object.keys(this.result)
            .filter((key) => !skip.has(key) && this.result[key] !== null && this.result[key] !== undefined && this.result[key] !== "")
            .map((key) => {
                const value = this.result[key];
                if (value && typeof value === "object" && !Array.isArray(value)) {
                    const nested = Object.keys(value)
                        .filter((k) => value[k] !== null && value[k] !== undefined && value[k] !== "")
                        .map((k) => ({ label: humanizeKey(k), value: formatValue(value[k]) }));
                    return { label: humanizeKey(key), value: "", nested };
                }
                return { label: humanizeKey(key), value: formatValue(value) };
            });
    }

    get statusLabel(): string {
        switch (this.status) {
            case "running": return "Running";
            case "paused": return "Paused";
            case "error": return "Needs Attention";
            case "done": return "Completed";
            case "aborted": return "Aborted";
            case "failed": return "Failed";
            default: return "";
        }
    }

    get statusBadgeClass(): string {
        switch (this.status) {
            case "running": return "badge-running";
            case "paused": return "badge-paused";
            case "error": return "badge-error";
            case "done": return "badge-done";
            case "aborted":
            case "failed": return "badge-failed";
            default: return "";
        }
    }

    // Per-row status in the steps table: every row except the last one has already been
    // passed, so it's always "Done" - only the last row reflects the run's live status.
    rowStatusLabel(last: boolean): string {
        if (!last) return "Done";
        switch (this.status) {
            case "running": return "Running";
            case "paused": return "Paused";
            case "error": return "Needs Attention";
            case "aborted": return "Aborted";
            case "failed": return "Failed";
            case "done":
            default: return "Done";
        }
    }

    rowStatusClass(last: boolean): string {
        if (!last) return "badge-done";
        return this.statusBadgeClass || "badge-running";
    }

    get isPaused(): boolean {
        return this.status === "paused";
    }

    get isError(): boolean {
        return this.status === "error";
    }

    get isTerminal(): boolean {
        return this.status === "done" || this.status === "aborted" || this.status === "failed";
    }

    get canPause(): boolean {
        return this.status === "running";
    }

    get canContinue(): boolean {
        return this.status === "paused" || this.status === "error";
    }

    async onPause() {
        if (!this.handle || this.busy) return;
        this.busy = true;
        try {
            await this.handle.pause();
        } finally {
            this.busy = false;
        }
    }

    async onContinue(singleStep = false) {
        if (!this.handle || this.busy) return;
        this.busy = true;
        try {
            await this.handle.continue(singleStep);
        } finally {
            this.busy = false;
        }
    }

    async onAbort() {
        if (!this.handle || this.busy) return;
        this.busy = true;
        try {
            await this.handle.abort();
        } finally {
            this.busy = false;
        }
    }

    close() {
        this.modalRef.hide();
    }
}
